import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Checkbox,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import type { BookingData } from "../../../types/bookings";
import type {
  Package,
  AdditionalServicesCategories,
  AdditionalServiceItem,
  PackageSelectionProps,
} from "../../../types/package";
import apiService from "../../../service/Api";

// Interface for the service API response
interface ServiceResponse {
  id: number;
  name: string;
  description: string | null;
  price: string;
  service_category: string;
  service_category_display: string;
  created_at: string;
  updated_at: string;
}

// Interface for the paginated service response
interface PaginatedServiceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ServiceResponse[];
}

// Interface for the paginated package response
interface PackageBaseService {
  id: number;
  name: string;
  price?: string | number;
}

interface PaginatedPackageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    name: string;
    package_type: string;
    base_price: string;
    description: string;
    created_at: string;
    updated_at: string;
    company: number;
    base_services: PackageBaseService[];
  }[];
}

// Type guard for paginated package response
const isPaginatedPackageResponse = (
  data: unknown
): data is PaginatedPackageResponse => {
  return (
    typeof data === "object" &&
    data !== null &&
    "count" in data &&
    "next" in data &&
    "previous" in data &&
    "results" in data &&
    typeof data.count === "number" &&
    (typeof data.next === "string" || data.next === null) &&
    (typeof data.previous === "string" || data.previous === null) &&
    Array.isArray(data.results) &&
    data.results.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "id" in item &&
        "name" in item &&
        "base_price" in item &&
        "description" in item &&
        "base_services" in item &&
        typeof item.id === "number" &&
        typeof item.name === "string" &&
        typeof item.base_price === "string" &&
        typeof item.description === "string" &&
        Array.isArray(item.base_services)
    )
  );
};

// Type guard for paginated service response
const isPaginatedServiceResponse = (
  data: unknown
): data is PaginatedServiceResponse => {
  return (
    typeof data === "object" &&
    data !== null &&
    "count" in data &&
    "next" in data &&
    "previous" in data &&
    "results" in data &&
    typeof data.count === "number" &&
    (typeof data.next === "string" || data.next === null) &&
    (typeof data.previous === "string" || data.previous === null) &&
    Array.isArray(data.results) &&
    data.results.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "id" in item &&
        "name" in item &&
        "price" in item &&
        "service_category" in item &&
        typeof item.id === "number" &&
        typeof item.name === "string" &&
        typeof item.price === "string" &&
        typeof item.service_category === "string"
    )
  );
};

const AddBookingPackageSelection: React.FC<PackageSelectionProps> = ({
  bookingData,
  setBookingData,
  serviceType,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [packages, setPackages] = useState<Package[]>([]);
  const [additionalServicesConfig, setAdditionalServicesConfig] =
    useState<AdditionalServicesCategories>({});
  const [editedTotal, setEditedTotal] = useState<number | string>(
    bookingData.total || 0
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState<boolean>(true);
  const [isManualEdit, setIsManualEdit] = useState<boolean>(false);
  const isInitialMount = useRef(true);
  const lastCalculatedTotal = useRef<number>(0);

  // Ensure bookingData has valid status
  useEffect(() => {
    if (
      bookingData.status &&
      !["Enquiry", "Confirmed", "Fulfilled", "Cancelled"].includes(
        bookingData.status
      )
    ) {
      setBookingData((prev) => ({
        ...prev,
        status: "Enquiry",
      }));
    }
  }, [bookingData.status, setBookingData]);

  // Function to calculate totals
  const calculateTotals = useCallback(
    (data: BookingData) => {
      const packageTotal = data.package?.price || 0;
      let additionalTotal = 0;
      const allAdditionalServices: AdditionalServiceItem[] = [];

      Object.keys(additionalServicesConfig).forEach((category) => {
        const selected = data[category];
        if (Array.isArray(selected)) {
          selected.forEach((val) => {
            const service = additionalServicesConfig[category]?.find(
              (s) => s.value === val
            );
            if (service) {
              additionalTotal += service.price;
              allAdditionalServices.push({ ...service, category });
            }
          });
        }
      });

      return {
        packageTotal,
        additionalTotal,
        finalTotal: Number.parseFloat(
          (packageTotal + additionalTotal).toFixed(2)
        ),
        allAdditionalServices,
      };
    },
    [additionalServicesConfig]
  );

  useEffect(() => {
    if (isManualEdit || Object.keys(additionalServicesConfig).length === 0)
      return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const { packageTotal, additionalTotal, finalTotal, allAdditionalServices } =
      calculateTotals(bookingData);

    if (Math.abs(lastCalculatedTotal.current - finalTotal) > 0.01) {
      lastCalculatedTotal.current = finalTotal;
      setEditedTotal(finalTotal);

      const timeoutId = setTimeout(() => {
        setBookingData((prev) => {
          if (
            Math.abs((prev.total || 0) - finalTotal) > 0.01 ||
            Math.abs((prev.package_total || 0) - packageTotal) > 0.01 ||
            Math.abs((prev.additional_services_total || 0) - additionalTotal) >
              0.01
          ) {
            return {
              ...prev,
              total: finalTotal,
              package_total: packageTotal,
              additional_services_total: additionalTotal,
              additional_services: allAdditionalServices,
            };
          }
          return prev;
        });
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [
    bookingData,
    additionalServicesConfig,
    calculateTotals,
    isManualEdit,
    setBookingData,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 🧠 Determine the backend package_type based on serviceType
        const serviceTypeMap: Record<string, string | string[]> = {
          FUNERAL: ["Funeral"],
          FUNERAL_HOME: "FuneralHome",
          CHURCH: ["Church"],
          CONFERENCE: "Conference",
          EVENT: "Event",
          OTHER: "Other",
        };

        const selectedType = serviceTypeMap[serviceType] || "";

        let fetchedPackages: Package[] = [];

        if (Array.isArray(selectedType)) {
          const requests = selectedType.map((type) =>
            apiService.get(`/api/packages/package/?package_type=${type}`)
          );
          const responses = await Promise.all(requests);

          fetchedPackages = responses.flatMap((res) => {
            const data = res.data;
            if (!isPaginatedPackageResponse(data)) {
              throw new Error("Invalid package data format");
            }
            return data.results.map((item) => ({
              id: item.id,
              name: item.name,
              price: Number.parseFloat(item.base_price) || 0,
              contents: item.description
                ? item.description.split("\n").filter((line) => line.trim())
                : [],
              package_type: item.package_type || "Funeral",
              base_services: item.base_services,
            }));
          });
        } else {
          // Single type
          const packageResponse = await apiService.get(
            `/api/packages/package/?package_type=${selectedType}`
          );
          const packageData = packageResponse.data;
          if (!isPaginatedPackageResponse(packageData)) {
            throw new Error("Invalid package data format");
          }
          fetchedPackages = packageData.results.map((item) => ({
            id: item.id,
            name: item.name,
            price: Number.parseFloat(item.base_price) || 0,
            contents: item.description
              ? item.description.split("\n").filter((line) => line.trim())
              : [],
            package_type: item.package_type || selectedType,
            base_services: item.base_services,
          }));
        }

        // 🏛️ Handle St. George’s Chapel special case
        let filtered = fetchedPackages;
        if (bookingData?.name?.includes("St George's College Chapel")) {
          const stGeorgesPackages = fetchedPackages.filter((pkg) => {
            const pkgName = pkg.name?.toLowerCase() || "";
            const pkgType = pkg.package_type?.toLowerCase() || "";
            const pkgDescription = pkg.description?.toLowerCase() || "";
            return (
              pkgName.includes("st georges") ||
              pkgType.includes("st georges") ||
              pkgDescription.includes("st georges")
            );
          });

          if (stGeorgesPackages.length > 0) {
            filtered = stGeorgesPackages;
          }
        }

        setPackages(filtered);

        // 🧾 Set default package if none selected
        if (filtered.length > 0 && !bookingData.package?.id) {
          const defaultPackage = filtered[0];
          const packageTotal = defaultPackage.price;
          const total = packageTotal;

          lastCalculatedTotal.current = total;
          setEditedTotal(total);

          setBookingData((prevData) => ({
            ...prevData,
            package: { ...defaultPackage },
            package_total: packageTotal,
            additional_services_total: 0,
            total,
          }));
        }

        const serviceResponse = await apiService.get(
          "/api/packages/package-service/"
        );
        const serviceData = serviceResponse.data;
        if (!isPaginatedServiceResponse(serviceData)) {
          throw new Error("Invalid service data format");
        }

        const groupedServices: AdditionalServicesCategories =
          serviceData.results.reduce((acc, item) => {
            const category = item.service_category || "Other";
            if (!acc[category]) acc[category] = [];
            acc[category].push({
              value: item.id.toString(),
              label: item.name,
              price: Number.parseFloat(item.price) || 0,
              description: item.description || undefined,
            });
            return acc;
          }, {} as AdditionalServicesCategories);

        setAdditionalServicesConfig(groupedServices);
      } catch (error: unknown) {
        console.error("Error fetching data:", error);
        const message =
          (error as { message?: string })?.message ||
          "Failed to load packages or services";
        setSnackbar({
          open: true,
          message,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    setEditedTotal(Number.parseFloat(String(bookingData.total)) || 0);
  }, [
    bookingData.name,
    bookingData.package?.id,
    bookingData.total,
    serviceType,
    setBookingData,
  ]);

  const handleTotalChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsManualEdit(true);
      const value = event.target.value.replace(/[^0-9.]/g, "");
      const newTotal = Number.parseFloat(value) || 0;
      setEditedTotal(value);
      lastCalculatedTotal.current = newTotal;
      setBookingData((prevData) => ({ ...prevData, total: newTotal }));
    },
    [setBookingData]
  );

  const handlePackageCardClick = useCallback(
    (selectedPackage: Package) => {
      setIsManualEdit(false);
      setBookingData((prevData) => ({
        ...prevData,
        package: { ...selectedPackage },
      }));
    },
    [setBookingData]
  );

  const handleAdditionalServiceChange = useCallback(
    (category: string, value: string, checked: boolean) => {
      setIsManualEdit(false);
      setBookingData((prevData) => {
        const updatedData: BookingData = { ...prevData };

        if (checked) {
          updatedData[category] = [
            ...((updatedData[category] as string[]) || []),
            value,
          ];
        } else {
          updatedData[category] = (updatedData[category] as string[])?.filter(
            (val: string) => val !== value
          );
        }

        if (!updatedData.package && packages.length > 0) {
          const defaultPackage = packages[0];
          updatedData.package = { ...defaultPackage };
        }
        return updatedData;
      });
    },
    [packages, setBookingData]
  );

  return (
    <Box p={3}>
      {loading ? (
        <Typography>Loading packages and services...</Typography>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Grid container spacing={2}>
              {packages.length === 0 ? (
                <Grid size={{ xs: 12 }}>
                  <Typography>No packages available</Typography>
                </Grid>
              ) : (
                packages.map((pkg) => (
                  <Grid size={{ xs: 4 }} key={pkg.id}>
                    <Box
                      onClick={() => handlePackageCardClick(pkg)}
                      sx={{
                        border: 2,
                        borderColor:
                          bookingData.package?.id === pkg.id
                            ? colors.primary[500]
                            : colors.primary[300],
                        borderRadius: 1,
                        mb: 2,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                        backgroundColor:
                          bookingData.package?.id === pkg.id
                            ? colors.secondary[500]
                            : "transparent",
                      }}
                    >
                      <Typography variant="subtitle1">{pkg.name}</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${pkg.price.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
            <Grid container>
              {Object.entries(additionalServicesConfig).length === 0 ? (
                <Grid size={{ xs: 12 }}>
                  <Typography>No additional services available</Typography>
                </Grid>
              ) : (
                Object.entries(additionalServicesConfig).map(
                  ([category, services]) => (
                    <Grid size={{ xs: 12, md: 6 }} key={`category-${category}`}>
                      <Box
                        sx={{
                          mb: 1,
                          mr: 1,
                          borderRadius: 1,
                          backgroundColor: colors.primary[800],
                          p: 2,
                        }}
                      >
                        <FormControl fullWidth>
                          <FormLabel sx={{ mb: 1 }}>{category}</FormLabel>
                          <FormGroup>
                            {services.map(({ value, label, price }) => (
                              <Box
                                key={`service-${category}-${value}`}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        (
                                          bookingData[category] as string[]
                                        )?.includes(value) || false
                                      }
                                      onChange={(e) =>
                                        handleAdditionalServiceChange(
                                          category,
                                          value,
                                          e.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={<Typography>{label}</Typography>}
                                  sx={{ flexGrow: 1 }}
                                />
                                <Typography>${price.toFixed(2)}</Typography>
                              </Box>
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Grid>
                  )
                )
              )}
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  borderRadius: 1,
                  backgroundColor: colors.primary[800],
                  p: 2,
                }}
              >
                <Typography variant="h6" mb={2}>
                  Included in this package
                </Typography>
                {bookingData?.package ? (
                  <>
                    {bookingData.package.contents?.map(
                      (item: string, index: number) => (
                        <Box
                          key={`content-${index}`}
                          sx={{
                            display: "flex",
                            justifyContent: "start",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            component="span"
                            ml={1}
                            sx={{ display: "inline-block" }}
                            dangerouslySetInnerHTML={{ __html: item }}
                          />
                        </Box>
                      )
                    )}
                    {(bookingData?.package?.base_services?.length ?? 0) > 0 && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" mb={1}>
                          Services included in this package:
                        </Typography>
                        {(bookingData.package?.base_services ?? []).map(
                          (service: PackageBaseService) => (
                            <Box
                              key={`service-${service.id}`}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography>{service.name}</Typography>
                              <Typography>
                                ${Number(service.price).toFixed(2)}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography>No items included</Typography>
                )}
              </Box>
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mt: 3,
                    borderRadius: 1,
                    backgroundColor: colors.primary[800],
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    height: 300,
                  }}
                >
                  <Box>
                    <Typography variant="h6" mt={4}>
                      Total{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {" "}
                        (including additional services)
                      </Typography>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                      <TextField
                        type="text"
                        value={`${editedTotal}`}
                        onChange={handleTotalChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          disableUnderline: true,
                        }}
                        variant="outlined"
                        sx={{
                          mr: 1,
                          height: "36px",
                          width: "120px",
                          "& .MuiInputBase-input": {
                            fontSize: "1.2rem",
                            fontWeight: "bold",
                            padding: "4px 6px",
                          },
                        }}
                      />
                    </Box>
                    {/* Display breakdown */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Package: ${(bookingData.package_total || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Additional Services: $
                        {(bookingData.additional_services_total || 0).toFixed(
                          2
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      sx={{
                        color: colors.primary[700],
                        borderColor: colors.primary[700],
                        border: 2,
                      }}
                    >
                      Generate Quotation
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: colors.primary[700],
                        color: colors.primary[700],
                        border: 2,
                      }}
                    >
                      Generate Invoice
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddBookingPackageSelection;
